import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Loader2, CheckCircle2, Info, Smartphone, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { abdmApi } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";

const ABHA_LOGO = "https://abdm.gov.in/img/NHA_Preloader.gif";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CONSENT_TEXT = "I, hereby declare that I am voluntarily sharing my Aadhaar number and demographic information issued by UIDAI with the National Health Authority (NHA) for the sole purpose of creating an ABHA number. I understand that my ABHA number and health records can be used and shared only with my explicit consent for the purposes notified by ABDM, including the provision of healthcare services.";

export default function AbhaLink() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const userId = user?._id;

  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [linked, setLinked] = useState(null);
  const [method, setMethod] = useState(null); // 'aadhaar' | 'mobile'
  const [step, setStep] = useState("choose"); // choose | otp | address | done
  const [busy, setBusy] = useState(false);

  // form state
  const [aadhaar, setAadhaar] = useState("");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [txnId, setTxnId] = useState("");
  const [existingAccounts, setExistingAccounts] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [suffix, setSuffix] = useState("@sbx");
  const [chosenAddr, setChosenAddr] = useState("");
  const [consent, setConsent] = useState(false);
  const [addr, setAddr] = useState({ abhaAddress: "", password: "", firstName: "", lastName: "", yearOfBirth: "", gender: "M" });

  useEffect(() => {
    (async () => {
      try {
        const [cfgRes, linkRes] = await Promise.all([
          abdmApi.status(),
          userId ? abdmApi.linked(userId) : Promise.resolve({ data: { linked: false } }),
        ]);
        setConfig(cfgRes.data);
        setLinked(linkRes.data?.abha && linkRes.data?.linked ? linkRes.data.abha : null);
      } catch (e) {
        // non-fatal
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  const notReady = config && (!config.credentials_set || !config.proxy_set);

  const handleErr = (e) => {
    const msg = e?.response?.data?.detail || e?.message || "Something went wrong";
    toast.error(typeof msg === "string" ? msg : "Request failed");
  };

  const requestOtp = async () => {
    setBusy(true);
    try {
      if (method === "aadhaar") {
        const digits = aadhaar.replace(/\D/g, "");
        if (digits.length !== 12) return toast.error("Enter a valid 12-digit Aadhaar");
        const res = await abdmApi.aadhaarRequestOtp({ patient_id: userId, aadhaar: digits });
        setTxnId(res.data.txnId);
      } else {
        const digits = mobile.replace(/\D/g, "");
        if (digits.length !== 10) return toast.error("Enter a valid 10-digit mobile");
        const res = await abdmApi.mobileRequestOtp({ patient_id: userId, mobile: digits });
        setTxnId(res.data.txnId);
      }
      setStep("otp");
      toast.success("OTP sent");
    } catch (e) {
      handleErr(e);
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async () => {
    if (otp.replace(/\D/g, "").length < 4) return toast.error("Enter the OTP");
    if (method === "aadhaar" && mobile.replace(/\D/g, "").length !== 10)
      return toast.error("Enter a valid 10-digit mobile number for your ABHA");
    setBusy(true);
    try {
      if (method === "aadhaar") {
        const res = await abdmApi.aadhaarVerifyOtp({ patient_id: userId, txnId, otp: otp.trim(), mobile: mobile.replace(/\D/g, "") });
        setLinked(res.data.abha);
        // M1: new enrolments pick a preferred ABHA address; existing ones are already set
        if (res.data.isNew && !res.data.abha?.abhaAddress) {
          try {
            const sug = await abdmApi.enrolSuggestions({ patient_id: userId, txnId });
            setSuggestions(sug.data.suggestions || []);
            setSuffix(sug.data.suffix || "@sbx");
            setChosenAddr((sug.data.suggestions || [])[0] || "");
            setStep("suggest");
          } catch (e) {
            // suggestions optional — ABHA number already created
            setStep("done");
          }
        } else {
          setStep("done");
        }
      } else {
        const res = await abdmApi.mobileVerifyOtp({ patient_id: userId, txnId, otp: otp.trim() });
        const accts = res.data.existingAccounts || [];
        setExistingAccounts(accts);
        setTxnId(res.data.txnId || txnId);
        setStep("address");
      }
    } catch (e) {
      handleErr(e);
    } finally {
      setBusy(false);
    }
  };

  const createAddress = async () => {
    if (!addr.abhaAddress || !addr.password) return toast.error("Enter an ABHA address and password");
    setBusy(true);
    try {
      const res = await abdmApi.createAddress({ patient_id: userId, txnId, mobile: mobile.replace(/\D/g, "") || undefined, ...addr });
      setLinked(res.data.abha);
      setStep("done");
    } catch (e) {
      handleErr(e);
    } finally {
      setBusy(false);
    }
  };

  const refreshSuggestions = async () => {
    setBusy(true);
    try {
      const sug = await abdmApi.enrolSuggestions({ patient_id: userId, txnId });
      setSuggestions(sug.data.suggestions || []);
      setChosenAddr((sug.data.suggestions || [])[0] || "");
    } catch (e) {
      handleErr(e);
    } finally {
      setBusy(false);
    }
  };

  const claimAddress = async () => {
    const username = (chosenAddr || "").trim().toLowerCase().replace(suffix, "");
    if (username.length < 4) return toast.error("Choose or enter an ABHA address");
    setBusy(true);
    try {
      const res = await abdmApi.enrolAbhaAddress({ patient_id: userId, txnId, abhaAddress: username });
      setLinked((prev) => ({ ...(prev || {}), abhaAddress: res.data.abhaAddress, abhaNumber: res.data.abhaNumber || prev?.abhaNumber }));
      toast.success("ABHA address created");
      setStep("done");
    } catch (e) {
      const msg = (e?.response?.data?.detail || "").toLowerCase();
      if (msg.includes("exist") || msg.includes("taken") || msg.includes("already")) {
        toast.error("That address is taken. Pick another or get fresh suggestions.");
        refreshSuggestions();
      } else {
        handleErr(e);
      }
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setMethod(null); setStep("choose"); setOtp(""); setTxnId(""); setAadhaar(""); setMobile(""); setExistingAccounts([]); setSuggestions([]); setChosenAddr(""); setConsent(false);
  };

  return (
    <div className="space-y-5" data-testid="abha-link-page">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate(-1)} className="h-9 w-9 rounded-xl hover:bg-accent flex items-center justify-center" data-testid="abha-back">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold">ABHA Health ID</h1>
          <p className="text-sm text-muted-foreground">Your Ayushman Bharat Digital Mission account</p>
        </div>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : linked ? (
        <Card className="rounded-3xl p-6 space-y-4" data-testid="abha-linked-card">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-2xl bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <p className="font-semibold text-lg">ABHA linked</p>
              <p className="text-sm text-muted-foreground">Your health identity is connected</p>
            </div>
          </div>
          <div className="rounded-2xl bg-muted/50 p-4 space-y-2 text-sm">
            {linked.name && <Row label="Name" value={linked.name} />}
            {linked.abhaNumber && <Row label="ABHA Number" value={linked.abhaNumber} testid="abha-number" />}
            {linked.abhaAddress && <Row label="ABHA Address" value={linked.abhaAddress} testid="abha-address" />}
            {linked.gender && <Row label="Gender" value={linked.gender} />}
            {linked.yearOfBirth && <Row label="Year of Birth" value={linked.yearOfBirth} />}
          </div>
        </Card>
      ) : (
        <>
          {notReady && (
            <Card className="rounded-2xl p-4 bg-amber-50 border-amber-200 flex gap-3" data-testid="abha-not-ready">
              <Info className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">ABHA linking is being enabled</p>
                <p>Secure connection to the ABDM sandbox is still being provisioned. You can explore the flow, but verification activates once setup is complete.</p>
              </div>
            </Card>
          )}

          <Card className="rounded-3xl p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-2xl bg-primary/10 flex items-center justify-center">
                <ShieldCheck className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-semibold">Create or link your ABHA</p>
                <p className="text-sm text-muted-foreground">A secure 14-digit health ID to share records across providers</p>
              </div>
            </div>

            {step === "choose" && (
              <div className="grid gap-3">
                <MethodButton active={method === "aadhaar"} onClick={() => setMethod("aadhaar")} icon={CreditCard} title="Using Aadhaar" desc="Verify with an OTP on your Aadhaar-linked mobile" testid="abha-method-aadhaar" />
                <MethodButton active={method === "mobile"} onClick={() => setMethod("mobile")} icon={Smartphone} title="Using Mobile Number" desc="Create an ABHA Address with a mobile OTP" testid="abha-method-mobile" />

                {method === "aadhaar" && (
                  <div className="space-y-2 pt-1">
                    <Label>Aadhaar Number</Label>
                    <Input inputMode="numeric" maxLength={14} placeholder="XXXX XXXX XXXX" value={aadhaar}
                      onChange={(e) => setAadhaar(e.target.value)} data-testid="abha-aadhaar-input" />
                    <p className="text-xs text-muted-foreground">Encrypted and sent securely to ABDM. We never store your Aadhaar.</p>
                  </div>
                )}
                {method === "mobile" && (
                  <div className="space-y-2 pt-1">
                    <Label>Mobile Number</Label>
                    <Input inputMode="numeric" maxLength={10} placeholder="10-digit mobile" value={mobile}
                      onChange={(e) => setMobile(e.target.value)} data-testid="abha-mobile-input" />
                  </div>
                )}

                {method && (
                  <label className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/40 rounded-xl p-3 cursor-pointer" data-testid="abha-consent-label">
                    <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                      className="mt-0.5 h-4 w-4 shrink-0" data-testid="abha-consent-checkbox" />
                    <span>{CONSENT_TEXT}</span>
                  </label>
                )}

                {method && (
                  <Button onClick={requestOtp} disabled={busy || !consent} className="rounded-full" data-testid="abha-send-otp">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Send OTP
                  </Button>
                )}
              </div>
            )}

            {step === "otp" && (
              <div className="space-y-3">
                {method === "aadhaar" && (
                  <div className="space-y-1">
                    <Label>Mobile Number <span className="text-red-500">*</span></Label>
                    <Input inputMode="numeric" maxLength={10} placeholder="10-digit mobile" value={mobile}
                      onChange={(e) => setMobile(e.target.value)} data-testid="abha-otp-mobile-input" />
                    <p className="text-xs text-muted-foreground">Required — this becomes your ABHA contact number.</p>
                  </div>
                )}
                <Label>Enter OTP</Label>
                <Input inputMode="numeric" maxLength={8} placeholder="6-digit code" value={otp}
                  onChange={(e) => setOtp(e.target.value)} data-testid="abha-otp-input" />
                <div className="flex gap-2">
                  <Button variant="outline" onClick={reset} className="rounded-full flex-1">Back</Button>
                  <Button onClick={verifyOtp} disabled={busy} className="rounded-full flex-1" data-testid="abha-verify-otp">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Verify
                  </Button>
                </div>
              </div>
            )}

            {step === "suggest" && (
              <div className="space-y-3" data-testid="abha-suggest-step">
                <div className="rounded-2xl bg-emerald-50 p-3 text-sm text-emerald-800">
                  <p className="font-medium">ABHA number created{linked?.abhaNumber ? `: ${linked.abhaNumber}` : ""}</p>
                  <p className="text-emerald-700/80">Pick your ABHA address — a friendly ID like name{suffix} you can share instead of the number.</p>
                </div>
                <Label>Choose an address</Label>
                <div className="grid grid-cols-1 gap-2">
                  {suggestions.map((s) => (
                    <button key={s} type="button" onClick={() => setChosenAddr(s)}
                      data-testid={`abha-suggestion-${s}`}
                      className={`flex items-center justify-between p-3 rounded-xl border text-left text-sm transition-colors ${chosenAddr === s ? "border-primary bg-primary/5 font-semibold" : "border-border hover:bg-accent"}`}>
                      <span>{s}{suffix}</span>
                      {chosenAddr === s && <CheckCircle2 className="h-4 w-4 text-primary" />}
                    </button>
                  ))}
                </div>
                <div className="space-y-1">
                  <Label>Or type your own</Label>
                  <div className="flex items-center gap-2">
                    <Input placeholder="your-id" value={chosenAddr.replace(suffix, "")}
                      onChange={(e) => setChosenAddr(e.target.value.toLowerCase().replace(suffix, ""))}
                      data-testid="abha-custom-address-input" />
                    <span className="text-sm text-muted-foreground">{suffix}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">8–18 chars incl. suffix · letters/digits · at most one dot & one underscore · cannot start with a digit.</p>
                </div>
                <Button onClick={claimAddress} disabled={busy} className="rounded-full w-full" data-testid="abha-claim-address">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Set ABHA Address
                </Button>
                <button onClick={refreshSuggestions} disabled={busy} className="text-xs text-primary font-medium w-full" data-testid="abha-refresh-suggestions">Get new suggestions</button>
                <button onClick={() => setStep("done")} className="text-xs text-muted-foreground w-full" data-testid="abha-skip-address">Skip for now</button>
              </div>
            )}

            {step === "address" && (
              <div className="space-y-3">
                {existingAccounts.length > 0 && (
                  <div className="rounded-2xl bg-muted/50 p-3 text-sm">
                    <p className="font-medium mb-1">Existing ABHA addresses on this number</p>
                    {existingAccounts.map((a, i) => (
                      <p key={i} className="text-muted-foreground">{a.abhaAddress || a.ABHAAddress || a.preferredAbhaAddress || JSON.stringify(a)}</p>
                    ))}
                  </div>
                )}
                <p className="text-sm font-medium">Create a new ABHA Address</p>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="First name" value={addr.firstName} onChange={(e) => setAddr({ ...addr, firstName: e.target.value })} data-testid="abha-firstname" />
                  <Input placeholder="Last name" value={addr.lastName} onChange={(e) => setAddr({ ...addr, lastName: e.target.value })} data-testid="abha-lastname" />
                  <Input placeholder="Year of birth" inputMode="numeric" maxLength={4} value={addr.yearOfBirth} onChange={(e) => setAddr({ ...addr, yearOfBirth: e.target.value })} data-testid="abha-yob" />
                  <select className="border rounded-md px-3 text-sm bg-background" value={addr.gender} onChange={(e) => setAddr({ ...addr, gender: e.target.value })} data-testid="abha-gender">
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                    <option value="O">Other</option>
                  </select>
                </div>
                <Input placeholder="your-id (ABHA address)" value={addr.abhaAddress} onChange={(e) => setAddr({ ...addr, abhaAddress: e.target.value })} data-testid="abha-address-input" />
                <Input type="password" placeholder="Set a password" value={addr.password} onChange={(e) => setAddr({ ...addr, password: e.target.value })} data-testid="abha-password-input" />
                <Button onClick={createAddress} disabled={busy} className="rounded-full w-full" data-testid="abha-create-address">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Create ABHA Address
                </Button>
              </div>
            )}

            {step === "done" && (
              <div className="text-center py-6 space-y-4" data-testid="abha-done-step">
                <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
                <p className="font-semibold text-lg">ABHA linked successfully</p>
                {userId && (
                  <div className="space-y-3">
                    <img src={`${BACKEND_URL}/api/abdm/abha/card/${userId}`} alt="ABHA card"
                      className="w-full max-w-sm mx-auto rounded-xl border shadow-sm" data-testid="abha-card-image"
                      onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    <div className="flex flex-col items-center gap-1">
                      <img src={`${BACKEND_URL}/api/abdm/abha/qr/${userId}`} alt="ABHA QR"
                        className="h-36 w-36 rounded-lg border bg-white p-2" data-testid="abha-qr-image"
                        onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      <p className="text-xs text-muted-foreground">Scan at any ABDM facility</p>
                    </div>
                  </div>
                )}
                <Button onClick={() => navigate("/records")} className="rounded-full" data-testid="abha-done-back">Back to Health Records</Button>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right" data-testid={testid}>{value}</span>
    </div>
  );
}

function MethodButton({ active, onClick, icon: Icon, title, desc, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex items-center gap-3 p-3 rounded-2xl border text-left transition-colors ${active ? "border-primary bg-primary/5" : "border-border hover:bg-accent"}`}>
      <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5 text-primary" />
      </div>
      <div>
        <p className="font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{desc}</p>
      </div>
    </button>
  );
}
